import React, { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { filterMenuItems, getBlockMenuItems, type BlockMenuItem } from '../blockMenuItems';
import { CATEGORY_ORDER } from '../blockRegistry';

interface SlashCommandMenuProps {
    editor: Editor;
    isOpen: boolean;
    query: string;
    position: { top: number; left: number };
    onSelect: (item: BlockMenuItem) => void;
    onClose: () => void;
    nested?: boolean;
}

/**
 * The "/" menu: type a slash in a text block, filter, pick with arrows + Enter
 * (ported from Scilora slash-command-menu).
 */
export const SlashCommandMenu: React.FC<SlashCommandMenuProps> = ({ editor, isOpen, query, position, onSelect, onClose, nested = false }) => {
    const translate = useTranslate();
    const menuRef = useRef<HTMLDivElement>(null);
    const items = filterMenuItems(getBlockMenuItems(translate, { nested }), query);
    const [selection, setSelection] = useState({ index: 0, forQuery: query });
    if (selection.forQuery !== query) {
        setSelection({ index: 0, forQuery: query });
    }
    const selectedIndex = selection.index;

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelection((prev) => ({ index: prev.index < items.length - 1 ? prev.index + 1 : 0, forQuery: prev.forQuery }));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelection((prev) => ({ index: prev.index > 0 ? prev.index - 1 : items.length - 1, forQuery: prev.forQuery }));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                setSelection((prev) => {
                    const item = items[prev.index];
                    if (item) requestAnimationFrame(() => onSelect(item));
                    return prev;
                });
            } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [isOpen, items, onSelect, onClose]);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);

    useEffect(() => {
        menuRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    useEffect(() => {
        if (!isOpen) return;
        const handleBlur = () => {
            setTimeout(() => {
                if (!menuRef.current?.contains(document.activeElement) && !editor.isFocused) onClose();
            }, 150);
        };
        editor.on('blur', handleBlur);
        return () => {
            editor.off('blur', handleBlur);
        };
    }, [isOpen, editor, onClose]);

    if (!isOpen || items.length === 0) return null;

    const grouped = new Map<string, { items: BlockMenuItem[]; startIdx: number }>();
    let idx = 0;
    for (const item of items) {
        if (!grouped.has(item.category)) grouped.set(item.category, { items: [], startIdx: idx });
        grouped.get(item.category)!.items.push(item);
        idx++;
    }

    return (
        <Paper
            ref={menuRef}
            elevation={4}
            className="oa-slash-menu"
            style={{ top: position.top, left: position.left }}
            role="listbox"
            aria-label={translate('blockEditor.slash.title')}
        >
            {query && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.75, display: 'block' }}>
                    {translate('blockEditor.slash.results', { smart_count: items.length, query })}
                </Typography>
            )}
            {CATEGORY_ORDER.filter((c) => grouped.has(c)).map((category) => {
                const group = grouped.get(category)!;
                return (
                    <Box key={category}>
                        <Typography variant="overline" sx={{ px: 1.5, py: 0.5, display: 'block', color: 'var(--oa-text-2)' }}>
                            {translate(`blockEditor.categories.${category}`)}
                        </Typography>
                        {group.items.map((item, i) => {
                            const globalIdx = group.startIdx + i;
                            const Icon = item.icon;
                            const active = globalIdx === selectedIndex;
                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    data-selected={active}
                                    className={`oa-slash-item${active ? ' is-active' : ''}`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => onSelect(item)}
                                    onMouseEnter={() => setSelection((prev) => ({ index: globalIdx, forQuery: prev.forQuery }))}
                                >
                                    <span className="oa-slash-icon"><Icon fontSize="small" /></span>
                                    <span className="oa-slash-text">
                                        <span className="oa-slash-label">{item.label}</span>
                                        <span className="oa-slash-desc">{item.description}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </Box>
                );
            })}
        </Paper>
    );
};
